/**
 * Central conversion tracking for OZ MOVE CO.
 * Sends phone-click and form-submission conversions to GA4 and Google Ads via gtag.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export const GOOGLE_ADS_ID = "AW-18337438177";

/** GA4 measurement ID, injected by the Google Analytics connector when configured. */
export const GA4_MEASUREMENT_ID: string | undefined =
  import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY || undefined;

/** Push to gtag if it is ready, otherwise queue on dataLayer so nothing is lost. */
export function gtagSafe(...args: unknown[]) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag === "function") {
    window.gtag(...args);
  } else {
    window.dataLayer.push(args);
  }
}

/** Load GA4 alongside the Google Ads tag already present in index.html. */
export function initGa4() {
  if (typeof document === "undefined" || !GA4_MEASUREMENT_ID) return;
  if (document.querySelector(`script[data-ga4="${GA4_MEASUREMENT_ID}"]`)) return;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  script.dataset.ga4 = GA4_MEASUREMENT_ID;
  document.head.appendChild(script);

  gtagSafe("js", new Date());
  gtagSafe("config", GA4_MEASUREMENT_ID);
}

/** Send a GA4 page_view on client-side route changes. */
export function trackPageView(path: string) {
  gtagSafe("event", "page_view", { page_path: path, page_location: window.location.href });
}

type ConversionPayload = Record<string, unknown>;

/** Raw (unhashed) lead identifiers collected from a form. */
export interface LeadIdentity {
  email?: string | null;
  phone?: string | null;
}

/** SHA-256 hex digest, required format for Google enhanced conversions. */
async function sha256Hex(value: string): Promise<string | undefined> {
  if (typeof crypto === "undefined" || !crypto.subtle) return undefined;
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Google normalisation: trim + lowercase. */
function normalizeEmail(email: string): string | undefined {
  const clean = email.trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean) ? clean : undefined;
}

/** Google normalisation: E.164, defaulting to Australia (+61). */
function normalizePhone(phone: string): string | undefined {
  let digits = phone.replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) return digits.length >= 9 ? digits : undefined;
  digits = digits.replace(/\D/g, "");
  if (digits.startsWith("61")) digits = digits.slice(2);
  else if (digits.startsWith("0")) digits = digits.slice(1);
  if (digits.length < 8 || digits.length > 10) return undefined;
  return `+61${digits}`;
}

/**
 * Enhanced conversions for leads: hash and hand the identifiers to gtag before
 * the conversion fires so Google Ads can match the lead offline.
 */
export async function setEnhancedConversionData(identity: LeadIdentity) {
  const userData: Record<string, string> = {};
  const email = identity.email ? normalizeEmail(identity.email) : undefined;
  const phone = identity.phone ? normalizePhone(identity.phone) : undefined;

  const [emailHash, phoneHash] = await Promise.all([
    email ? sha256Hex(email) : Promise.resolve(undefined),
    phone ? sha256Hex(phone) : Promise.resolve(undefined),
  ]);

  if (emailHash) userData.sha256_email_address = emailHash;
  if (phoneHash) userData.sha256_phone_number = phoneHash;
  if (!Object.keys(userData).length) return false;

  gtagSafe("set", "user_data", userData);
  return true;
}

/** Fire the same event to GA4 (named event) and Google Ads (conversion). */
export function trackConversion(eventName: string, payload: ConversionPayload = {}) {
  const base = {
    ...payload,
    page_path: typeof window !== "undefined" ? window.location.pathname : undefined,
  };
  gtagSafe("event", eventName, base);
  gtagSafe("event", "conversion", { send_to: GOOGLE_ADS_ID, event_label: eventName, ...base });
}

/** Quote wizard / contact form: a completed submission (primary conversion). */
export async function trackFormSubmit(
  formName: string,
  payload: ConversionPayload = {},
  identity?: LeadIdentity,
) {
  let enhanced = false;
  if (identity) {
    try {
      enhanced = await setEnhancedConversionData(identity);
    } catch {
      enhanced = false;
    }
  }
  trackConversion("generate_lead", { form_name: formName, enhanced_conversion: enhanced, ...payload });
  gtagSafe("event", "form_submit", { form_name: formName, ...payload });
}

/** Quote wizard step progression (micro-conversion, GA4 only). */
export function trackFormStep(formName: string, step: number, totalSteps: number) {
  gtagSafe("event", "form_step_complete", {
    form_name: formName,
    step,
    total_steps: totalSteps,
  });
}

/** A form started (first interaction), GA4 only. */
export function trackFormStart(formName: string) {
  gtagSafe("event", "form_start", { form_name: formName });
}
